import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Box, Typography, Tooltip, Button, IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import TodayIcon from '@material-ui/icons/Today';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import { otLabel } from '../../utils/otLabel';

const STATUS_COLORS = {
  pending:    { bg: '#94a3b8', solid: '#64748b', label: 'Pendente' },
  assigned:   { bg: '#3b82f6', solid: '#2563eb', label: 'Atribuída' },
  in_transit: { bg: '#f59e0b', solid: '#d97706', label: 'Em Trânsito' },
  delivered:  { bg: '#22c55e', solid: '#16a34a', label: 'Entregue' },
  completed:  { bg: '#22c55e', solid: '#16a34a', label: 'Concluída' },
  cancelled:  { bg: '#ef4444', solid: '#dc2626', label: 'Cancelada' },
};

// Day-view scale
const HOUR_START = 0;
const HOUR_END = 24;
const DAY_PX_PER_HOUR = 50;
// Week-view scale
const WEEK_DAYS = 7;
const WEEK_PX_PER_DAY = 220;
// Month-view scale
const MONTH_PX_PER_DAY = 64;

const LANE_HEIGHT = 42;
const HEADER_HEIGHT = 38;
const LEFT_COL_WIDTH = 180;
// Altura mínima de cada sub-pista quando uma faixa precisa de dividir-se
// para acomodar OTs sobrepostas (mesmo padrão do Google Calendar). Mantém
// a etiqueta legível mesmo com 3-4 OTs em paralelo.
const SUBROW_HEIGHT = 22;
const SUBROW_GAP = 2;

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d) {
  // Monday as week start (PT/EU convention)
  const x = startOfDay(d);
  const dow = x.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  return addDays(x, diff);
}
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function endOfMonth(d) { const x = new Date(d); x.setMonth(x.getMonth() + 1, 1); x.setHours(0, 0, 0, 0); return x; }
function daysInMonth(d) { return Math.round((endOfMonth(d) - startOfMonth(d)) / 86400000); }

/**
 * Lightweight Gantt timeline.
 *
 * Props:
 *   orders        — full OT list for the visible page
 *   onSelectOrder — callback when a bar is clicked (fn(order))
 *   lang          — 'pt' | 'en'
 */
function OrdersGanttView({ orders, onSelectOrder, lang = 'pt' }) {
  const [mode, setMode] = useState('day');        // 'day' | 'week' | 'month'
  const [groupBy, setGroupBy] = useState('vehicle'); // 'vehicle' | 'driver'
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const containerRef = useRef(null);

  // Visible range based on current mode
  const range = useMemo(() => {
    if (mode === 'month') {
      const start = startOfMonth(anchor);
      const end = endOfMonth(anchor);
      return { start, end };
    }
    if (mode === 'week') {
      const start = startOfWeek(anchor);
      const end = addDays(start, WEEK_DAYS);
      return { start, end };
    }
    const start = startOfDay(anchor);
    const end = addDays(start, 1);
    return { start, end };
  }, [mode, anchor]);
  const monthDays = mode === 'month' ? daysInMonth(anchor) : 0;

  // Default visible duration (ms) para OTs sem `deliveryWindowTo` —
  // tipicamente OTs de assistência, criadas com data/hora prevista mas
  // sem janela. Tenta usar `unloadingTime` (minutos) ou cai para 30 min.
  // Antes a OT era simplesmente descartada e o operador não a via.
  const DEFAULT_DURATION_MS = 30 * 60 * 1000;
  function resolveWindow(o) {
    const wf = o.deliveryWindowFrom ? new Date(o.deliveryWindowFrom).getTime() : null;
    if (!wf) return null;
    let wt = o.deliveryWindowTo ? new Date(o.deliveryWindowTo).getTime() : null;
    if (wt == null) {
      const unloadingMin = Number(o.unloadingTime);
      const durMs = Number.isFinite(unloadingMin) && unloadingMin > 0
        ? unloadingMin * 60 * 1000
        : DEFAULT_DURATION_MS;
      wt = wf + durMs;
    } else if (wt <= wf) {
      // Janela degenerada (igual ou invertida) — também ganha duração mínima.
      wt = wf + DEFAULT_DURATION_MS;
    }
    return { wf, wt };
  }

  // Em vista SEMANA e MÊS, cada OT é renderizada como um chip que enche
  // o(s) dia(s) onde se realiza — independentemente da hora exacta. Sem
  // isto uma OT de 30 min ocupa ≈4.6 px na semana (220 px/dia) e <2 px no
  // mês (64 px/dia) — "bolinhas" invisíveis. Padrão calendário (Google
  // Calendar / Outlook): nestas vistas mostra-se em que dias há trabalho,
  // não a hora dentro do dia. A vista DIA mantém escala horária.
  function snapWindowForMode(w, m) {
    if (m === 'day') return w;
    const startD = new Date(w.wf); startD.setHours(0, 0, 0, 0);
    // -1ms para que uma janela que acaba exactamente à meia-noite não conte
    // como "atinge o dia seguinte" (fim exclusivo).
    const endD = new Date(w.wt - 1); endD.setHours(0, 0, 0, 0);
    endD.setDate(endD.getDate() + 1);
    return { wf: startD.getTime(), wt: endD.getTime() };
  }

  // Group orders by vehicle plate or driver name + atribui sub-pista a
  // cada OT por interval-scheduling: ordens sobrepostas no tempo ganham
  // sub-pistas distintas (track 0, 1, 2…), nunca colidem visualmente.
  // A altura da faixa cresce com o número máximo de tracks paralelas.
  const lanes = useMemo(() => {
    const map = new Map();
    const rangeStart = range.start.getTime();
    const rangeEnd = range.end.getTime();
    orders.forEach(o => {
      const wRaw = resolveWindow(o);
      if (!wRaw) return;
      // No mês, o sub-row scheduling tem de usar a janela "snapped" para que
      // OTs no mesmo dia colidam (independentemente da hora) e fiquem em
      // tracks distintos.
      const w = snapWindowForMode(wRaw, mode);
      if (w.wt < rangeStart || w.wf > rangeEnd) return;

      let key, label, sub;
      if (groupBy === 'driver') {
        key = o.driverName || '__none_driver__';
        label = o.driverName || (lang === 'pt' ? 'Sem motorista' : 'No driver');
        sub = o.vehiclePlate || '';
      } else {
        key = o.vehiclePlate || '__none_vehicle__';
        label = o.vehiclePlate || (lang === 'pt' ? 'Sem veículo' : 'No vehicle');
        sub = o.driverName || '';
      }
      if (!map.has(key)) map.set(key, { key, label, sub, items: [] });
      // wRaw preservada para o tooltip mostrar a hora real (não a snapped).
      map.get(key).items.push({ o, w, wRaw });
    });

    // Para cada faixa: ordena por início e atribui o índice da primeira
    // sub-pista cujo último fim é <= ao início desta OT. Caso nenhuma
    // sirva, cria-se uma nova sub-pista. O resultado é a forma canónica
    // de mostrar eventos paralelos em calendários sem sobreposição.
    const lanesArr = [];
    for (const lane of map.values()) {
      const items = [...lane.items].sort((a, b) => a.w.wf - b.w.wf);
      const trackEnds = []; // trackEnds[i] = último wt visto naquela sub-pista
      const orders = items.map(({ o, w, wRaw }) => {
        let track = trackEnds.findIndex(end => end <= w.wf);
        if (track === -1) { track = trackEnds.length; trackEnds.push(w.wt); }
        else trackEnds[track] = w.wt;
        return { ...o, _track: track, _wf: w.wf, _wt: w.wt, _wfRaw: wRaw.wf, _wtRaw: wRaw.wt };
      });
      lanesArr.push({
        key: lane.key, label: lane.label, sub: lane.sub,
        orders,
        trackCount: Math.max(1, trackEnds.length),
      });
    }

    return lanesArr.sort((a, b) => {
      if (a.key.startsWith('__none')) return 1;
      if (b.key.startsWith('__none')) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [orders, range.start, range.end, groupBy, lang, mode]);

  // Helper — altura efectiva da faixa em função do nº de sub-pistas. A
  // faixa nunca encolhe abaixo de LANE_HEIGHT; cresce de SUBROW_HEIGHT
  // por cada sub-pista extra para que cada barra mantenha legibilidade.
  function laneHeightPx(trackCount) {
    if (trackCount <= 1) return LANE_HEIGHT;
    return Math.max(LANE_HEIGHT, trackCount * SUBROW_HEIGHT + (trackCount - 1) * SUBROW_GAP + 12);
  }

  // Geometry calculation — maps a time range (já resolvido em ms) para
  // {leftPx, widthPx}. Aceita ms directos para que `resolveWindow` (que
  // já trata fallbacks de janela em OTs sem `To`) seja a única fonte.
  function barGeom(startMs, endMs) {
    const rangeStart = range.start.getTime();
    const rangeEnd = range.end.getTime();
    const start = Math.max(startMs, rangeStart);
    const end = Math.min(endMs, rangeEnd);
    if (end <= start) return null;
    if (mode === 'month') {
      const totalMs = rangeEnd - rangeStart;
      const totalPx = monthDays * MONTH_PX_PER_DAY;
      const leftPx = ((start - rangeStart) / totalMs) * totalPx;
      const widthPx = ((end - start) / totalMs) * totalPx;
      return { leftPx, widthPx };
    }
    if (mode === 'week') {
      const totalMs = rangeEnd - rangeStart;
      const leftPx = ((start - rangeStart) / totalMs) * (WEEK_DAYS * WEEK_PX_PER_DAY);
      const widthPx = ((end - start) / totalMs) * (WEEK_DAYS * WEEK_PX_PER_DAY);
      return { leftPx, widthPx };
    }
    const leftPx = ((start - rangeStart) / 3600000) * DAY_PX_PER_HOUR;
    const widthPx = ((end - start) / 3600000) * DAY_PX_PER_HOUR;
    return { leftPx, widthPx };
  }

  const totalWidth = mode === 'month'
    ? monthDays * MONTH_PX_PER_DAY
    : mode === 'week'
      ? WEEK_DAYS * WEEK_PX_PER_DAY
      : (HOUR_END - HOUR_START) * DAY_PX_PER_HOUR;

  // Scroll to "now" on mount / when switching to today
  useEffect(() => {
    if (!containerRef.current) return;
    const now = new Date();
    if (mode === 'day') {
      const today = startOfDay(now);
      if (today.getTime() !== startOfDay(anchor).getTime()) return;
      const hours = now.getHours() + now.getMinutes() / 60;
      containerRef.current.scrollLeft = Math.max(0, hours * DAY_PX_PER_HOUR - 200);
    } else if (mode === 'week') {
      const weekStart = startOfWeek(anchor);
      const weekEnd = addDays(weekStart, WEEK_DAYS);
      if (now < weekStart || now > weekEnd) return;
      const ratio = (now.getTime() - weekStart.getTime()) / (weekEnd.getTime() - weekStart.getTime());
      containerRef.current.scrollLeft = Math.max(0, ratio * totalWidth - 300);
    } else {
      // month
      if (now < range.start || now > range.end) return;
      const ratio = (now.getTime() - range.start.getTime()) / (range.end.getTime() - range.start.getTime());
      containerRef.current.scrollLeft = Math.max(0, ratio * totalWidth - 300);
    }
  }, [mode, anchor, totalWidth, range.start, range.end]);

  // "Now" vertical line
  const now = new Date();
  let nowOffsetPx = null;
  if (now >= range.start && now <= range.end) {
    if (mode === 'day') {
      nowOffsetPx = (now.getHours() + now.getMinutes() / 60) * DAY_PX_PER_HOUR;
    } else {
      const ratio = (now.getTime() - range.start.getTime()) / (range.end.getTime() - range.start.getTime());
      nowOffsetPx = ratio * totalWidth;
    }
  }

  // Navigation deltas (depend on mode)
  const stepDays = mode === 'week' ? 7 : mode === 'month' ? null : 1;
  function navigate(direction) {
    if (mode === 'month') {
      setAnchor(d => { const x = new Date(d); x.setMonth(x.getMonth() + direction); return x; });
    } else {
      setAnchor(d => addDays(d, direction * stepDays));
    }
  }

  // Range label
  const rangeLabel = mode === 'month'
    ? anchor.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })
    : mode === 'week'
      ? `${range.start.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })} — ${addDays(range.start, 6).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}`
      : range.start.toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  function ToggleButton({ active, onClick, children }) {
    return (
      <Button size="small" onClick={onClick}
        style={{
          fontSize: '0.68rem', fontWeight: 700, textTransform: 'none',
          padding: '3px 12px', borderRadius: 6, minWidth: 'auto',
          background: active ? '#fff' : 'transparent',
          color: active ? '#6366f1' : '#94a3b8',
          boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
        }}>
        {children}
      </Button>
    );
  }

  return (
    <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', margin: '0 24px 16px' }}>
      {/* Toolbar */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ChevronLeftIcon style={{ fontSize: 18 }} />
        </IconButton>
        <Button size="small" startIcon={<TodayIcon style={{ fontSize: 14 }} />}
          onClick={() => setAnchor(startOfDay(new Date()))}
          style={{ fontSize: '0.72rem', textTransform: 'none', color: '#6366f1', fontWeight: 700 }}>
          {lang === 'pt' ? 'Hoje' : 'Today'}
        </Button>
        <IconButton size="small" onClick={() => navigate(1)}>
          <ChevronRightIcon style={{ fontSize: 18 }} />
        </IconButton>
        <Typography style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', marginLeft: 6, textTransform: 'capitalize' }}>
          {rangeLabel}
        </Typography>

        <Box style={{ flex: 1 }} />

        {/* Group-by toggle */}
        <Box style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 2, border: '1px solid #e2e8f0' }}>
          <ToggleButton active={groupBy === 'vehicle'} onClick={() => setGroupBy('vehicle')}>
            <LocalShippingIcon style={{ fontSize: 12, marginRight: 4, verticalAlign: 'middle' }} />
            {lang === 'pt' ? 'Veículo' : 'Vehicle'}
          </ToggleButton>
          <ToggleButton active={groupBy === 'driver'} onClick={() => setGroupBy('driver')}>
            <PersonIcon style={{ fontSize: 12, marginRight: 4, verticalAlign: 'middle' }} />
            {lang === 'pt' ? 'Motorista' : 'Driver'}
          </ToggleButton>
        </Box>

        {/* Mode toggle */}
        <Box style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 2, border: '1px solid #e2e8f0' }}>
          <ToggleButton active={mode === 'day'} onClick={() => setMode('day')}>
            {lang === 'pt' ? 'Dia' : 'Day'}
          </ToggleButton>
          <ToggleButton active={mode === 'week'} onClick={() => setMode('week')}>
            {lang === 'pt' ? 'Semana' : 'Week'}
          </ToggleButton>
          <ToggleButton active={mode === 'month'} onClick={() => setMode('month')}>
            {lang === 'pt' ? 'Mês' : 'Month'}
          </ToggleButton>
        </Box>

        <Typography style={{ fontSize: '0.68rem', color: '#94a3b8', marginLeft: 6 }}>
          {lanes.length} {groupBy === 'vehicle' ? (lang === 'pt' ? 'veículos · ' : 'vehicles · ') : (lang === 'pt' ? 'motoristas · ' : 'drivers · ')}
          {lanes.reduce((sum, l) => sum + l.orders.length, 0)} {lang === 'pt' ? 'ordens' : 'orders'}
        </Typography>
      </Box>

      {/* Gantt body */}
      <Box style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Left — lanes labels */}
        <Box style={{ width: LEFT_COL_WIDTH, flexShrink: 0, borderRight: '2px solid #e2e8f0', background: '#fafbfc' }}>
          <Box style={{ height: HEADER_HEIGHT, display: 'flex', alignItems: 'center', padding: '0 14px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9' }}>
            <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {groupBy === 'vehicle' ? (lang === 'pt' ? 'Veículo' : 'Vehicle') : (lang === 'pt' ? 'Motorista' : 'Driver')}
            </Typography>
          </Box>
          {lanes.length === 0 ? (
            <Box style={{ padding: 20, textAlign: 'center', color: '#cbd5e1', fontSize: '0.7rem', fontStyle: 'italic' }}>
              {lang === 'pt' ? 'Nenhuma ordem neste período' : 'No orders in this range'}
            </Box>
          ) : lanes.map(lane => (
            <Box key={lane.key} style={{
              height: laneHeightPx(lane.trackCount), display: 'flex', flexDirection: 'column',
              justifyContent: 'center', padding: '0 14px', borderBottom: '1px solid #f1f5f9',
              background: lane.key.startsWith('__none') ? '#fef3c7' : '#fff',
            }}>
              <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {groupBy === 'vehicle' ? (
                  <LocalShippingIcon style={{ fontSize: 13, color: lane.key.startsWith('__none') ? '#f59e0b' : '#64748b' }} />
                ) : (
                  <PersonIcon style={{ fontSize: 13, color: lane.key.startsWith('__none') ? '#f59e0b' : '#8b5cf6' }} />
                )}
                {lane.label}
              </Typography>
              {lane.sub && (
                <Typography style={{ fontSize: '0.56rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginLeft: 19 }}>
                  {groupBy === 'vehicle' ? '👤 ' : '🚚 '}{lane.sub}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Right — scrollable timeline */}
        <Box ref={containerRef} style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', position: 'relative' }}>
          <Box style={{ width: totalWidth, position: 'relative' }}>
            {/* Header */}
            <Box style={{
              height: HEADER_HEIGHT, position: 'sticky', top: 0, zIndex: 2,
              background: '#f1f5f9', borderBottom: '1px solid #e2e8f0',
              display: 'flex',
            }}>
              {mode === 'day' ? (
                Array.from({ length: HOUR_END - HOUR_START }, (_, i) => {
                  const h = HOUR_START + i;
                  return (
                    <Box key={h} style={{
                      width: DAY_PX_PER_HOUR, flexShrink: 0, position: 'relative',
                      borderLeft: h % 6 === 0 ? '2px solid #cbd5e1' : '1px solid #e2e8f0',
                      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: h % 6 === 0 ? '#1e293b' : '#94a3b8' }}>
                        {String(h).padStart(2, '0')}:00
                      </Typography>
                    </Box>
                  );
                })
              ) : mode === 'week' ? (
                Array.from({ length: WEEK_DAYS }, (_, i) => {
                  const d = addDays(range.start, i);
                  const isToday = startOfDay(new Date()).getTime() === d.getTime();
                  return (
                    <Box key={i} style={{
                      width: WEEK_PX_PER_DAY, flexShrink: 0, position: 'relative',
                      borderLeft: '2px solid #cbd5e1',
                      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 6,
                      background: isToday ? '#eef2ff' : 'transparent',
                    }}>
                      <Typography style={{ fontSize: '0.66rem', fontWeight: 800, color: isToday ? '#6366f1' : '#1e293b', textTransform: 'uppercase' }}>
                        {d.toLocaleDateString('pt-PT', { weekday: 'short' })}
                      </Typography>
                      <Typography style={{ fontSize: '0.66rem', fontWeight: 600, color: isToday ? '#6366f1' : '#64748b' }}>
                        {d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                      </Typography>
                    </Box>
                  );
                })
              ) : (
                // Month view — one column per day of the month
                Array.from({ length: monthDays }, (_, i) => {
                  const d = addDays(range.start, i);
                  const isToday = startOfDay(new Date()).getTime() === d.getTime();
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <Box key={i} style={{
                      width: MONTH_PX_PER_DAY, flexShrink: 0, position: 'relative',
                      borderLeft: i === 0 || d.getDay() === 1 ? '2px solid #cbd5e1' : '1px solid #e2e8f0',
                      height: '100%', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      background: isToday ? '#eef2ff' : (isWeekend ? '#f8fafc' : 'transparent'),
                    }}>
                      <Typography style={{ fontSize: '0.54rem', fontWeight: 700, color: isToday ? '#6366f1' : (isWeekend ? '#94a3b8' : '#475569'), textTransform: 'uppercase', lineHeight: 1 }}>
                        {d.toLocaleDateString('pt-PT', { weekday: 'short' }).slice(0, 3)}
                      </Typography>
                      <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: isToday ? '#6366f1' : '#1e293b', lineHeight: 1.2 }}>
                        {d.getDate()}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Body: lanes */}
            {lanes.map((lane, li) => (
              <Box key={lane.key} style={{
                height: laneHeightPx(lane.trackCount), position: 'relative',
                borderBottom: '1px solid #f1f5f9',
                background: li % 2 === 0 ? '#fff' : '#fafbfc',
              }}>
                {/* Grid lines */}
                {mode === 'day'
                  ? Array.from({ length: HOUR_END - HOUR_START }, (_, i) => (
                      <Box key={i} style={{
                        position: 'absolute', top: 0, bottom: 0, left: i * DAY_PX_PER_HOUR,
                        borderLeft: (HOUR_START + i) % 6 === 0 ? '2px solid #e2e8f0' : '1px solid #f1f5f9',
                        pointerEvents: 'none',
                      }} />
                    ))
                  : mode === 'week'
                    ? Array.from({ length: WEEK_DAYS }, (_, i) => (
                        <Box key={i} style={{
                          position: 'absolute', top: 0, bottom: 0, left: i * WEEK_PX_PER_DAY,
                          borderLeft: '2px solid #e2e8f0', pointerEvents: 'none',
                        }} />
                      ))
                    : Array.from({ length: monthDays }, (_, i) => {
                        const d = addDays(range.start, i);
                        const isWeekStart = d.getDay() === 1;
                        return (
                          <Box key={i} style={{
                            position: 'absolute', top: 0, bottom: 0, left: i * MONTH_PX_PER_DAY,
                            borderLeft: isWeekStart ? '2px solid #cbd5e1' : '1px solid #f1f5f9',
                            pointerEvents: 'none',
                          }} />
                        );
                      })}

                {/* Order bars */}
                {lane.orders.map(o => {
                  // _wf/_wt já calculados na construção das lanes (track
                  // assignment usou-os). Voltamos a passá-los a barGeom.
                  const wf = o._wf, wt = o._wt;
                  const geom = barGeom(wf, wt);
                  if (!geom) return null;
                  const st = STATUS_COLORS[o.status] || STATUS_COLORS.pending;
                  // Posição vertical da barra: empilha por sub-pista quando
                  // há sobreposição. Faixa só-com-uma-pista mantém o look
                  // anterior (centrada com 6 px de padding em cima/baixo).
                  const trackCount = lane.trackCount;
                  let barTop, barHeight;
                  if (trackCount <= 1) {
                    barTop = 6;
                    barHeight = LANE_HEIGHT - 12;
                  } else {
                    barTop = 6 + o._track * (SUBROW_HEIGHT + SUBROW_GAP);
                    barHeight = SUBROW_HEIGHT;
                  }
                  // Semana e Mês: a barra enche a célula do(s) dia(s)
                  // (janela snapped) menos um padding para se ver o gap.
                  // Dia mantém escala horária. Tooltip usa hora REAL.
                  let leftPx = geom.leftPx;
                  let widthPx;
                  if (mode === 'month') {
                    widthPx = Math.max(geom.widthPx - 6, MONTH_PX_PER_DAY - 10);
                    leftPx += 3;
                  } else if (mode === 'week') {
                    widthPx = Math.max(geom.widthPx - 12, WEEK_PX_PER_DAY - 18);
                    leftPx += 6;
                  } else {
                    widthPx = Math.max(geom.widthPx, 18);
                  }
                  return (
                    <Tooltip key={o.id || o._id} title={
                      <Box>
                        <Box style={{ fontSize: '0.78rem', fontWeight: 700 }}>{otLabel(o)}</Box>
                        <Box style={{ fontSize: '0.68rem' }}>{(o.pickupName || '').split(',')[0]} → {(o.deliveryName || '').split(',')[0]}</Box>
                        <Box style={{ fontSize: '0.65rem', opacity: 0.85 }}>
                          {/* Mostra a janela REAL — _wfRaw — não a snapped */}
                          {new Date(o._wfRaw).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          {o.deliveryWindowTo && new Date(o.deliveryWindowTo).getTime() > o._wfRaw ? (
                            <>{' → '}{new Date(o.deliveryWindowTo).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</>
                          ) : null}
                        </Box>
                        <Box style={{ fontSize: '0.65rem', opacity: 0.85, marginTop: 3 }}>{st.label}</Box>
                      </Box>
                    }>
                      <Box
                        onClick={() => onSelectOrder && onSelectOrder(o)}
                        style={{
                          position: 'absolute',
                          left: leftPx,
                          width: widthPx,
                          top: barTop,
                          height: barHeight,
                          background: `linear-gradient(135deg, ${st.bg}, ${st.solid})`,
                          borderRadius: 8,
                          boxShadow: `0 2px 6px ${st.bg}40`,
                          border: '1px solid rgba(255,255,255,0.3)',
                          display: 'flex', alignItems: 'center',
                          padding: '0 8px',
                          cursor: 'pointer', overflow: 'hidden',
                          transition: 'transform 0.15s, box-shadow 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${st.bg}60`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 2px 6px ${st.bg}40`; }}>
                        <Typography style={{
                          // Chips enchem a célula em semana/mês, por isso
                          // recuperam tamanhos legíveis. Antes ficavam
                          // 0.54rem assumindo barras estreitas.
                          fontSize: mode === 'day' ? '0.65rem'
                            : mode === 'week' ? '0.68rem'
                            : '0.6rem',
                          fontWeight: 800, color: '#fff',
                          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {otLabel(o)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            ))}

            {/* "Now" vertical line */}
            {nowOffsetPx != null && (
              <Box style={{
                position: 'absolute',
                top: HEADER_HEIGHT, bottom: 0,
                left: nowOffsetPx,
                width: 2, background: '#ef4444',
                boxShadow: '0 0 6px rgba(239,68,68,0.5)',
                pointerEvents: 'none', zIndex: 3,
              }}>
                <Box style={{
                  position: 'absolute', top: -6, left: -5,
                  width: 12, height: 12, borderRadius: '50%',
                  background: '#ef4444', border: '2px solid #fff',
                  boxShadow: '0 0 6px rgba(239,68,68,0.5)',
                }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default OrdersGanttView;
